import service from '@/utils/request'
import { getApiBaseUrl } from '@/utils/env'
import type {
  CurrentSessionResp,
  GuideQuestion,
  SessionContentDeltaResp,
  SessionListPageDTO,
  SessionContentPageDTO,
} from '@/types/chat'

/**
 * 发送聊天消息（流式 SSE）
 * 这个函数仅返回 SSE URL，实际参数通过 SSE connect 的 body 传递
 */
export function sendChatMessage() {
  // 返回 SSE 连接的完整 URL
  const baseURL = getApiBaseUrl()
  return `${baseURL}/aifacade/aichat/session/inputV2`
}

/**
 * 获取引导问题列表
 */
export function getGuideQuestionList() {
  return service.get<GuideQuestion[]>('/aifacade/aichat/getGuideQuestionList')
}

/**
 * 创建会话响应（与后端 CreateSessionRespDTO 对齐）
 */
export interface CreateSessionResp {
  sessionId: number
  sessionStatus: number
  isNew: boolean
}

/**
 * 同步创建 AI 会话（20260528-fix）
 *
 * 用于「点击发送」时若本地无 sessionId，先拿到 sessionId 再发起 SSE，
 * 避免 Q1+Q2 短间隔下 SSE 首帧 session 事件返回前 Q2 也带 null sessionId
 * → 后端各自创建出两个独立 session 的分裂问题。
 */
export function createSession() {
  return service.post<CreateSessionResp>('/aifacade/aichat/session/create')
}

/**
 * 分页获取历史会话列表
 * @param current 当前页码（从1开始）
 * @param size 每页大小
 */
export function getHisSessionList(params: { current?: number; size?: number }) {
  return service.post<SessionListPageDTO>('/aifacade/aichat/getHisSessionList', params)
}

export interface SessionDeleteResp {
  sessionId: number
  deleted: boolean
  idempotent: boolean
}

export function deleteSession(data: { sessionId: number | string; operationId: string }) {
  return service.post<SessionDeleteResp>('/aifacade/aichat/session/delete', data)
}

/**
 * 批量点赞或点踩内容
 * @param pointList 点赞点踩列表
 */
export interface PointItem {
  contentId: string
  pointType: number // 1-点赞，2-点踩
}

export function pointContent(data: { pointList: PointItem[] }) {
  return service.post<boolean>('/aifacade/aichat/batchPointContent', data)
}

/**
 * 分页获取会话内容
 * @param current 当前页码（从1开始）
 * @param size 每页大小
 * @param sessionId 会话ID
 */
export function getSessionContent(params: { current?: number; size?: number; sessionId: number | string }) {
  return service.post<SessionContentPageDTO>('/aifacade/aichat/getSessionContent', params)
}

export function getCurrentSession() {
  return service.get<CurrentSessionResp>('/aifacade/aichat/session/current')
}

export function getSessionContentDelta(params: {
  sessionId: string
  lastContentId: string
  limit?: number
}) {
  return service.get<SessionContentDeltaResp>('/aifacade/aichat/session/content/delta', { params })
}

/**
 * AI 对话 BUSY 状态查询响应（20260519 AI 对话回答中禁止追问发送）。
 *
 * - busy=true：当前用户处于 AI 回答中，前端进入忙态 UI + 2s 轮询
 * - busy=false：可正常发送（含 Redis 不可达降级，前端无感知）
 */
export interface BusyStatusResp {
  busy: boolean
  sessionId: number | null
  startAt: number | null
}

/**
 * 查询当前用户的 AI 对话 BUSY 状态（FR-02 / AC-09）。
 *
 * <p>P95 < 200ms（PRD AC-13），路径 GET /aifacade/aichat/session/busy-status</p>
 */
export function getBusyStatus() {
  return service.get<BusyStatusResp>('/aifacade/aichat/session/busy-status')
}
